import { supabase } from '@/lib/supabase';

export interface PropBotStats {
  totalPropBots: number;
  activePropBots: number;
  totalConnectedAccounts: number;
  activeConnectedAccounts: number;
  totalCapital: number;
  reversePositionAccounts: number;
  topPerformingBot: {
    name: string;
    accountCount: number;
  } | null;
  growthRate: number;
}

export const propBotStatsService = {
  async getPropBotStats(): Promise<PropBotStats> {
    try {
      // Get prop bots count
      const { count: totalPropBots } = await supabase
        .from('bots')
        .select('*', { count: 'exact', head: true })
        .eq('type', 'prop_bot')
        .neq('is_deleted', true);

      const { count: activePropBots } = await supabase
        .from('bots')
        .select('*', { count: 'exact', head: true })
        .eq('type', 'prop_bot')
        .eq('status', 'active')
        .neq('is_deleted', true);

      // Get all prop bot accounts (including shadow bots)
      // Shadow bots have original_bot_id pointing to prop bots
      const { data: propBotIds } = await supabase
        .from('bots')
        .select('id')
        .eq('type', 'prop_bot')
        .neq('is_deleted', true);

      if (!propBotIds || propBotIds.length === 0) {
        return {
          totalPropBots: totalPropBots || 0,
          activePropBots: activePropBots || 0,
          totalConnectedAccounts: 0,
          activeConnectedAccounts: 0,
          totalCapital: 0,
          reversePositionAccounts: 0,
          topPerformingBot: null,
          growthRate: 0
        };
      }

      const propBotIdList = propBotIds.map(bot => bot.id);

      // Get connected accounts (both direct and shadow)
      const { data: botAccounts } = await supabase
        .from('bot_accounts')
        .select(`
          id,
          bot_id,
          original_bot_id,
          status,
          added_date,
          api_connection_id
        `)
        .or(`bot_id.in.(${propBotIdList.join(',')}),original_bot_id.in.(${propBotIdList.join(',')})`);

      const totalConnectedAccounts = botAccounts?.length || 0;
      const activeConnectedAccounts = botAccounts?.filter(acc => acc.status === 'active').length || 0;
      
      // Reverse position = shadow bots (bot_id != original_bot_id)
      const reversePositionAccounts = botAccounts?.filter(acc => 
        acc.bot_id !== acc.original_bot_id && acc.original_bot_id
      ).length || 0;

      // Calculate total capital from trading accounts
      let totalCapital = 0;
      if (botAccounts && botAccounts.length > 0) {
        const apiConnectionIds = botAccounts
          .map(acc => acc.api_connection_id)
          .filter(id => id);

        if (apiConnectionIds.length > 0) {
          const { data: tradingAccounts } = await supabase
            .from('trading_accounts')
            .select('balance_account')
            .in('api_connection_id', apiConnectionIds);

          totalCapital = tradingAccounts?.reduce((sum, acc) => 
            sum + (acc.balance_account || 0), 0
          ) || 0;
        }
      }

      // Get top performing bot from bot_prop_details
      const { data: topBotData } = await supabase
        .from('bot_prop_details')
        .select(`
          connected_accounts_total,
          bots!inner(name, id)
        `)
        .order('connected_accounts_total', { ascending: false })
        .limit(1);

      let topPerformingBot = null;
      if (topBotData && topBotData.length > 0 && topBotData[0].connected_accounts_total > 0) {
        const topBot = topBotData[0];
        topPerformingBot = {
          name: (topBot.bots as any).name,
          accountCount: topBot.connected_accounts_total
        };
      }

      // Calculate growth rate (30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

      const { count: recentAccounts } = await supabase
        .from('bot_accounts')
        .select('*', { count: 'exact', head: true })
        .gte('added_date', thirtyDaysAgo.toISOString())
        .or(`bot_id.in.(${propBotIdList.join(',')}),original_bot_id.in.(${propBotIdList.join(',')})`);

      const { count: previousAccounts } = await supabase
        .from('bot_accounts')
        .select('*', { count: 'exact', head: true })
        .lt('added_date', thirtyDaysAgo.toISOString())
        .or(`bot_id.in.(${propBotIdList.join(',')}),original_bot_id.in.(${propBotIdList.join(',')})`);

      const growthRate = previousAccounts > 0 
        ? ((recentAccounts || 0) / previousAccounts) * 100 
        : 0;

      return {
        totalPropBots: totalPropBots || 0,
        activePropBots: activePropBots || 0,
        totalConnectedAccounts,
        activeConnectedAccounts,
        totalCapital,
        reversePositionAccounts,
        topPerformingBot,
        growthRate: Number(growthRate.toFixed(1))
      };
    } catch (error) {
      console.error('Error fetching prop bot stats:', error);
      throw error;
    }
  }
};